import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Gift, Percent, Users, Volume2 } from "lucide-react";
import MaroonNavbar from "@/components/new/MaroonNavbar";
import MaroonFooter from "@/components/new/MaroonFooter";
import WhatsAppButton from "@/components/new/WhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = { maroon: "#621B21", maroonDark: "#5C0F1C", yellow: "#F5C800", beige: "#F5EDD6", white: "#FFFFFF" };
const LOGO = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/xwzvhvs5_Header%20Logo%20Ethia.png";
const CHANGEMAKER_VIDEO = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/dn7dfmxg_%23ETHIAChangemaker.mp4";
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/l81fuiu1_Pet%20Food%20Redefined%20Video.mp4";

const TOTAL_SPOTS = 1000;
const BASE_COUNT = 864;

const useReveal = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => { const el = ref.current; if (!el) return; const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.2 }); o.observe(el); return () => o.disconnect(); }, []);
  return [ref, vis];
};

/* ─── Sub-components ─── */
const WhyEthiaSection = () => {
  const [ref, vis] = useReveal();
  return (
    <section ref={ref} className="py-14 md:py-24" style={{ background: C.beige }} data-testid="ea-benefits">
      <div className="max-w-[1100px] mx-auto px-6">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>Why ETHIA?</h2>
        <p className="text-center text-sm md:text-base mb-10" style={{ color: `${C.maroon}99` }}>Your pet deserves cleaner meat.</p>
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-5 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {[
            { title: "Made with Real Chicken Cells", desc: "Drawn from actual chicken cells in a controlled environment. Real chicken — grown safely. Clean, lab-raised nutrition for your pet." },
            { title: "100% Antibiotic Free", desc: "No antibiotics, no byproducts, no fillers. Same protein your dog loves without the slaughter." },
            { title: "100% Cruelty Free", desc: "Zero animals harmed. No factory farms. Just real chicken — grown safely, without slaughter." },
          ].map((item, i) => (
            <div key={i} className="rounded-2xl p-6 md:p-8" style={{ background: C.maroon }} data-testid={`ea-benefit-${i}`}>
              <h3 className="text-base md:text-lg font-bold mb-2" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>{item.title}</h3>
              <p className="text-sm" style={{ color: `${C.beige}99` }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const EAVideoSection = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  return (
    <section className="py-14 md:py-24" style={{ background: C.maroon }} data-testid="ea-video-section">
      <div className="max-w-[900px] mx-auto px-6">
        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
          <video ref={videoRef} src={VIDEO_URL} autoPlay muted loop playsInline className="w-full aspect-video bg-black" data-testid="ea-video-player" />
          <button onClick={() => { if (videoRef.current) { videoRef.current.muted = !muted; setMuted(!muted); } }} className="absolute bottom-4 right-4 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: C.yellow }}>
            <Volume2 size={18} style={{ color: C.maroon }} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ─── Community Email Form ─── */
const CommunityForm = () => {
  const [showForm, setShowForm] = useState(false);
  const [communityEmail, setCommunityEmail] = useState("");
  const [communityLoading, setCommunityLoading] = useState(false);
  const [communityDone, setCommunityDone] = useState(false);

  const handleCommunitySubmit = async (e) => {
    e.preventDefault();
    setCommunityLoading(true);
    try {
      await axios.post(`${API}/community-signup`, { email: communityEmail });
      setCommunityDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setCommunityLoading(false);
    }
  };

  return (
    <div className="mt-6">
      {!showForm && !communityDone && (
        <button onClick={() => setShowForm(true)} className="inline-block font-bold px-6 py-3 rounded-full text-sm transition-all hover:scale-105" style={{ background: C.yellow, color: C.maroon, fontFamily: "Manrope, sans-serif" }} data-testid="ea-community-btn">
          Join our community for pet food updates <ArrowRight className="inline ml-1" size={16} />
        </button>
      )}
      {showForm && !communityDone && (
        <form onSubmit={handleCommunitySubmit} className="flex gap-2 max-w-sm" data-testid="ea-community-form">
          <input type="email" value={communityEmail} onChange={e => setCommunityEmail(e.target.value)} required placeholder="Email Address" className="flex-1 border-2 rounded-xl px-4 py-2.5 text-sm outline-none" style={{ borderColor: `${C.beige}60`, background: "rgba(255,255,255,0.1)", color: C.beige }} data-testid="ea-community-email" />
          <button type="submit" disabled={communityLoading} className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-105 disabled:opacity-50" style={{ background: C.yellow, color: C.maroon }} data-testid="ea-community-submit">{communityLoading ? "..." : "Submit"}</button>
        </form>
      )}
      {communityDone && (
        <p className="text-sm font-medium" style={{ color: C.yellow }} data-testid="ea-community-success">You are on the list. We will be in touch. &#128062;</p>
      )}
    </div>
  );
};

const EarlyAccessPage = () => {
  const [email, setEmail] = useState("");
  const [petType, setPetType] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [signupCount, setSignupCount] = useState(BASE_COUNT);
  const formRef = useRef(null);

  useEffect(() => {
    document.title = "ETHIA | Early Access - Join the First 1000";
    (async () => {
      try { const res = await axios.get(`${API}/early-access/count`); setSignupCount(BASE_COUNT + (res.data.count || 0)); } catch {}
    })();
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!petType) { toast.error("Please select your pet type"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/early-access`, { email, pet_type: petType });
      setSubmitted(true);
      setSignupCount(p => p + 1);
      toast.success("You're in! Welcome to the ETHIA family.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const spotsLeft = TOTAL_SPOTS - signupCount;
  const progressPercent = Math.min((signupCount / TOTAL_SPOTS) * 100, 100);

  return (
    <div className="min-h-screen" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="early-access-page">
      <MaroonNavbar />

      {/* ─── 1. Hero ─── */}
      <section className="relative pt-28 pb-14 md:pt-40 md:pb-24 overflow-hidden" style={{ background: C.maroonDark }} data-testid="ea-hero">
        <div className="absolute top-20 right-10 w-64 h-64 md:w-96 md:h-96 rounded-full blur-3xl opacity-10 animate-breathe" style={{ background: C.yellow }} />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-6" style={{ background: `${C.yellow}18`, border: `1px solid ${C.yellow}40`, color: C.yellow }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#22C55E" }} />
            LIMITED TO 1,000 PET PARENTS
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-4 md:mb-6" style={{ color: C.white, fontFamily: "Fraunces, serif" }} data-testid="ea-headline">
            You just saved <span style={{ color: C.yellow }}>a life.</span>
          </h1>
          <p className="text-sm md:text-base leading-relaxed mb-8 max-w-xl mx-auto" style={{ color: `${C.beige}CC` }}>
            As a thank you for leading this shift, we are locking in an upto 30% founding member discount* for life. We are inviting 1000 dog parents to join us as founding members of the #ETHIAChangemaker movement. Secure your spot and lock in your reward for choosing a kinder path.
          </p>
          <a href="/products" className="inline-block font-bold px-8 py-4 md:px-10 md:py-5 rounded-full text-base md:text-lg transition-all duration-300 hover:scale-105 shadow-lg" style={{ background: C.yellow, color: C.maroon }} data-testid="ea-hero-cta">
            Be A Changemaker <ArrowRight className="inline ml-2" size={20} />
          </a>
          {/* Progress bar */}
          <div className="mt-10 md:mt-14 max-w-sm mx-auto">
            <div className="flex justify-between text-sm mb-2">
              <span style={{ color: C.yellow }}><span className="font-bold">{signupCount}</span> joined</span>
              <span style={{ color: `${C.beige}99` }}>{spotsLeft > 0 ? `${spotsLeft} spots left` : "Almost full!"}</span>
            </div>
            <div className="w-full rounded-full h-3 overflow-hidden" style={{ background: `${C.white}15` }}>
              <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${progressPercent}%`, background: C.yellow }} data-testid="ea-progress-bar" />
            </div>
          </div>
          <p className="text-[10px] mt-6 max-w-xl mx-auto" style={{ color: `${C.beige}60` }}>*T&C: Discount is subject to order volume and does not automatically qualify for every order placed. Exclusive to the first 1,000 pet parents only.</p>
        </div>
      </section>

      {/* ─── 2. Why ETHIA ─── */}
      <WhyEthiaSection />

      {/* ─── 3. From Cell to Bowl ─── */}
      <section className="py-14 md:py-24" style={{ background: C.maroon }} data-testid="ea-cell-to-bowl">
        <div className="max-w-[1100px] mx-auto px-5 md:px-6">
          <div className="text-center mb-8">
            <span className="text-xs font-bold tracking-[3px] uppercase" style={{ color: C.yellow, fontFamily: "Manrope, sans-serif" }}>STRAIGHT FROM OUR SOCIALS</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
            <div className="flex justify-center"><video src={CHANGEMAKER_VIDEO} autoPlay muted loop playsInline className="w-full max-w-xs md:max-w-sm rounded-2xl shadow-2xl" data-testid="ea-cell-video" /></div>
            <div className="text-center md:text-left">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>From Cell to Bowl.</h2>
              <p className="text-xl md:text-2xl font-bold mb-5" style={{ color: C.yellow, fontFamily: "Fraunces, serif" }}>Purely Science.</p>
              <div className="space-y-3 mb-6">
                {["Real chicken cells, cultivated in a nutrient-rich environment", "Same protein, same taste — without the slaughter", "Made in India, with love for Pets"].map((t, i) => (
                  <div key={i} className="flex items-start gap-3 text-left">
                    <Check size={18} className="mt-0.5 flex-shrink-0" style={{ color: C.yellow }} />
                    <span className="text-sm md:text-base" style={{ color: `${C.beige}CC` }}>{t}</span>
                  </div>
                ))}
              </div>
              <CommunityForm />
            </div>
          </div>
        </div>
      </section>

      {/* ─── 4. Taste the Future ─── */}
      <section className="py-14 md:py-24" style={{ background: C.maroonDark }} data-testid="ea-perks">
        <div className="max-w-[1000px] mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Taste the Future! <span style={{ color: C.yellow }}>It's on us.</span></h2>
          <p className="text-sm md:text-base mb-10" style={{ color: `${C.beige}CC` }}>Join now and unlock exclusive perks.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {[
              { icon: <Gift size={30} />, title: "Free Trial Sample", desc: "Get a complimentary sample pouch when we launch" },
              { icon: <Percent size={30} />, title: "Upto 30% Founder Discount*", desc: "Exclusive pricing based on order volume. Only for early access members." },
              { icon: <Users size={30} />, title: "Testing Group", desc: "Be part of our exclusive product testing community" },
            ].map((perk, i) => (
              <div key={i} className="rounded-2xl p-6 md:p-8" style={{ background: `${C.maroon}80` }} data-testid={`ea-perk-${i}`}>
                <div className="flex justify-center mb-4" style={{ color: C.yellow }}>{perk.icon}</div>
                <h3 className="text-base md:text-lg font-bold mb-2" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>{perk.title}</h3>
                <p className="text-sm" style={{ color: `${C.beige}99` }}>{perk.desc}</p>
              </div>
            ))}
          </div>
          <button onClick={scrollToForm} className="font-bold px-10 py-4 rounded-full transition-all hover:scale-105" style={{ background: C.yellow, color: C.maroon }} data-testid="ea-perks-cta">
            Claim Your Spot <ArrowRight className="inline ml-1" size={18} />
          </button>
        </div>
      </section>

      {/* ─── 5. Video ─── */}
      <EAVideoSection />

      {/* ─── 6. Claim Form ─── */}
      <section ref={formRef} className="py-14 md:py-24" style={{ background: C.beige }} data-testid="ea-form-section">
        <div className="max-w-lg mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>Claim Your Founders Discount</h2>
          {!submitted ? (
            <div className="rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg" style={{ background: C.white }}>
              <h3 className="text-xl font-bold text-center mb-2" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>Claim Your Spot &#10022;</h3>
              <p className="text-sm text-center mb-8" style={{ color: `${C.maroon}99` }}>Only {spotsLeft > 0 ? spotsLeft : "a few"} spots remaining. Takes 10 seconds.</p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: C.maroon }}>Email Address *</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" className="w-full border-0 border-b-2 bg-transparent py-3 text-sm outline-none transition-colors" style={{ borderColor: `${C.maroon}40`, color: C.maroon }} onFocus={e => e.target.style.borderColor = C.maroon} onBlur={e => e.target.style.borderColor = `${C.maroon}40`} data-testid="ea-input-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: C.maroon }}>Pet Type *</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ v: "dog", l: "Dog" }, { v: "cat", l: "Cat" }].map(opt => (
                      <button key={opt.v} type="button" onClick={() => setPetType(opt.v)} className="py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 border-2" style={{ background: petType === opt.v ? C.yellow : "transparent", color: petType === opt.v ? C.maroon : `${C.maroon}99`, borderColor: petType === opt.v ? C.yellow : `${C.maroon}25` }} data-testid={`ea-pet-${opt.v}`}>
                        {opt.v === "dog" ? "🐕" : "🐈"} {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
                <button type="submit" disabled={loading} className="w-full font-bold py-4 rounded-full text-base transition-all duration-300 hover:scale-[1.02] shadow-lg disabled:opacity-50" style={{ background: C.yellow, color: C.maroon }} data-testid="ea-submit-btn">
                  {loading ? "Joining..." : "Join Early Access →"}
                </button>
              </form>
              {/* Progress inside form */}
              <div className="mt-6">
                <div className="flex justify-between text-xs mb-2">
                  <span style={{ color: `${C.maroon}99` }}><span className="font-bold" style={{ color: C.maroon }}>{signupCount}</span> of {TOTAL_SPOTS} spots filled</span>
                  <span style={{ color: `${C.maroon}99` }}>{Math.round(progressPercent)}%</span>
                </div>
                <div className="w-full rounded-full h-2.5 overflow-hidden" style={{ background: `${C.maroon}15` }}>
                  <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${progressPercent}%`, background: C.yellow }} />
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl md:rounded-3xl p-8 md:p-12 text-center shadow-lg" style={{ background: C.white, border: `2px solid ${C.yellow}` }} data-testid="ea-success">
              <div className="text-5xl mb-4">🎉</div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>You're In!</h2>
              <p className="text-sm md:text-base mb-6" style={{ color: `${C.maroon}99` }}>
                Welcome to the ETHIA family. You're <span className="font-bold" style={{ color: C.maroon }}>#{signupCount}</span> on the early access list.
              </p>
              <a href="/" className="inline-block font-bold px-8 py-3 rounded-full text-sm hover:scale-105 transition-transform" style={{ background: C.yellow, color: C.maroon }} data-testid="ea-explore-btn">Explore ETHIA →</a>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 md:py-20" style={{ background: C.beige }}>
        <div className="max-w-[800px] mx-auto px-6">
          <div className="rounded-2xl md:rounded-3xl p-8 md:p-14 text-center" style={{ background: C.maroonDark }}>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Be First. Feed Better. Save Lives. &#128062;</h3>
            <p className="text-sm md:text-base mb-8" style={{ color: `${C.beige}CC` }}>Be the first in India to feed your dog food that saves lives.</p>
            <a href="/products" className="inline-block font-bold px-10 py-4 rounded-full transition-all hover:scale-105 text-sm md:text-base" style={{ background: C.yellow, color: C.maroon }}>Order Now <ArrowRight className="inline ml-1" size={18} /></a>
          </div>
        </div>
      </section>

      <MaroonFooter />
      <WhatsAppButton />
    </div>
  );
};

export default EarlyAccessPage;
